import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useLocale } from "@/hooks/useLocale";
import { toast } from "sonner";

interface Props {
  adventureId: string;
  currentUserId: string | null;
  players: any[];
  onClose: () => void;
  onCreated: () => void;
}

export function InteractionCreateForm({ adventureId, currentUserId, players, onClose, onCreated }: Props) {
  const { t } = useLocale();
  const [newType, setNewType] = useState("trade_offer");
  const [newTarget, setNewTarget] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [selectedOfferedItems, setSelectedOfferedItems] = useState<string[]>([]);
  const [selectedRequestedItems, setSelectedRequestedItems] = useState<string[]>([]);

  const otherPlayers = players.filter((p) => p.user_id !== currentUserId);
  const myPlayer = players.find((p) => p.user_id === currentUserId);

  // Get inventory from character_data (includes currency items)
  const myInventory: string[] = useMemo(() => {
    const inv = myPlayer?.character_data?.inventory || [];
    return inv.map((item: any) => typeof item === "string" ? item : item.name || "Unknown Item");
  }, [myPlayer]);

  // Get target's inventory for trade requests
  const targetPlayer = players.find((p) => p.user_id === newTarget);
  const targetInventory: string[] = useMemo(() => {
    const inv = targetPlayer?.character_data?.inventory || [];
    return inv.map((item: any) => typeof item === "string" ? item : item.name || "Unknown Item");
  }, [targetPlayer]);

  const toggleItem = (item: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
  };

  const handleCreate = async () => {
    if (!currentUserId || !newTarget) return;

    const payload: any = { message: newMessage.trim() };
    if (newType === "trade_offer") {
      payload.offered_items = selectedOfferedItems;
      payload.requested_items = selectedRequestedItems;
    } else if (newType === "gift") {
      payload.offered_items = selectedOfferedItems;
    }

    const { error } = await supabase.from("adventure_interactions").insert({
      adventure_id: adventureId,
      from_user_id: currentUserId,
      to_user_id: newTarget,
      type: newType,
      payload,
    } as any);

    if (error) { toast.error(t.play_send_failed); return; }
    onCreated();
  };

  return (
    <div className="flex flex-col h-full p-3 gap-2 overflow-auto">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={onClose}>
          ← {t.back}
        </Button>
        <span className="text-xs font-semibold">{t.mp_new_interaction}</span>
      </div>

      <Select value={newType} onValueChange={setNewType}>
        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="trade_offer">{t.mp_trade}</SelectItem>
          <SelectItem value="gift">{t.mp_gift}</SelectItem>
          <SelectItem value="challenge">{t.mp_challenge}</SelectItem>
          <SelectItem value="alliance_request">{t.mp_alliance}</SelectItem>
        </SelectContent>
      </Select>

      <Select value={newTarget} onValueChange={setNewTarget}>
        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder={t.mp_select_player} /></SelectTrigger>
        <SelectContent>
          {otherPlayers.map((p) => (
            <SelectItem key={p.user_id} value={p.user_id}>
              {p.character_name || p.profile?.full_name || t.play_anonymous}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {(newType === "trade_offer" || newType === "gift") && (
        <>
          {/* Offered items from own inventory (includes currency items) */}
          {myInventory.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase">{t.mp_offering}</span>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-auto">
                {myInventory.map((item) => (
                  <label key={item} className="flex items-center gap-1 text-[10px] cursor-pointer bg-muted/30 rounded px-1.5 py-0.5 border border-border hover:bg-accent/30 transition-colors">
                    <Checkbox
                      checked={selectedOfferedItems.includes(item)}
                      onCheckedChange={() => toggleItem(item, selectedOfferedItems, setSelectedOfferedItems)}
                      className="h-3 w-3"
                    />
                    {item}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Requested items from target inventory (trade only) */}
          {newType === "trade_offer" && newTarget && targetInventory.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase">{t.mp_requesting}</span>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-auto">
                {targetInventory.map((item) => (
                  <label key={item} className="flex items-center gap-1 text-[10px] cursor-pointer bg-muted/30 rounded px-1.5 py-0.5 border border-border hover:bg-accent/30 transition-colors">
                    <Checkbox
                      checked={selectedRequestedItems.includes(item)}
                      onCheckedChange={() => toggleItem(item, selectedRequestedItems, setSelectedRequestedItems)}
                      className="h-3 w-3"
                    />
                    {item}
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={t.mp_interaction_message} className="h-7 text-xs" />

      <Button onClick={handleCreate} disabled={!newTarget} size="sm" className="self-end h-7 text-xs">
        {t.play_send}
      </Button>
    </div>
  );
}
